import loadable from '@loadable/component'

// Utils
import { isBrowser } from '@process'
import { IAudioContext, TAudioContextConstructor } from 'standardized-audio-context'
import { safeLocalStorage as localStorage } from 'utils'

const toneImport = loadable.lib(() => import('tone'))
const standardizedAudioContextImport = loadable.lib(() => import('standardized-audio-context'))

type Tone = any // eslint-disable-line
class Loop {
  trackId = 0
  beatStart = 0
  beatCount = 0
}

const MEDIA_ERR = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE ',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
}

const resumeAudioContext = (tone: Tone) => () => {
  if (tone && tone.context && tone.context.state === 'suspended') {
    tone.context.resume().then(() => {
      ;['mouseup', 'touchend'].forEach(event => {
        document.body.removeEventListener(event, resumeAudioContext(tone))
      })
    })
  }
}

class AudioBackend {
  trackUrl = ''
  errorMessages: Array<string> = []
  state: {
    paused: boolean
    loaded: boolean
    looping: boolean
    playing: boolean
    shouldHalt: boolean
    beatPositions: Array<number>
  }
  audioElement: HTMLAudioElement | null
  currentLoop: Loop | null
  context: null | IAudioContext
  tone: Tone | null
  player: any // eslint-disable-line
  soundBuffer: any // eslint-disable-line
  req: XMLHttpRequest | null
  beatCount: number
  volume: number

  loopStartedAtContextTime = 0

  constructor() {
    this.state = {
      paused: false,
      loaded: false,
      looping: false,
      playing: false,
      shouldHalt: false,
      beatPositions: [],
    }
    this.audioElement = null
    this.currentLoop = null
    this.context = null
    this.tone = null

    this.req = null
    this.beatCount = 0
    this.volume = +localStorage.getItem('volume') || 1
    this._ensureAudioElement()
    ;['mouseup', 'touchend'].forEach(event => {
      if (this.tone) {
        document.body.addEventListener(event, resumeAudioContext(this.tone))
      }
    })
  }

  resetState = (): void => {
    this.state = {
      paused: false,
      loaded: false,
      looping: false,
      playing: false,
      shouldHalt: false,
      beatPositions: [],
    }
  }

  getCurrentLoopTime = (): number => {
    const loopDuration = this.player.loopEnd - this.player.loopStart
    return this.player.loopStart + ((this.player.context.currentTime - this.loopStartedAtContextTime) % loopDuration)
  }

  getCurrentLoopStartTime = (): number => {
    const { beatPositions } = this.state
    if (beatPositions && this.currentLoop) {
      return beatPositions[this.currentLoop.beatStart]
    }
    return 0
  }

  getCurrentTime = (): number => {
    if (this.state.looping && this.currentLoop) {
      return this.getCurrentLoopTime()
    }
    if (this.audioElement) {
      return this.audioElement.currentTime
    }
    return 0
  }

  isPaused = (): boolean => this.state.paused

  isPlaying = (): boolean => this.state.loaded && !this.state.paused

  setPaused = (paused: boolean) => {
    if (this.state.looping) {
      if (paused) {
        this.pauseLoop()
      } else if (this.currentLoop) {
        this.playLoop(this.currentLoop)
      }
    } else if (this.audioElement) {
      if (paused) {
        this.audioElement.pause()
      } else {
        this.audioElement.play()
      }
    }
    this.state.paused = paused
  }

  setVolume = (volume: number) => {
    if (this.tone?.getDestination()?.volume) {
      const gain = 20.0 * Math.log10(volume) // NOTE(Kitti): it's MATH bro
      this.tone.getDestination().volume.value = gain
    }
    if (this.audioElement) {
      this.audioElement.volume = volume
    }
  }

  _getAudioElementError = (): string =>
    this.audioElement && this.audioElement.error && this.audioElement.error.code
      ? MEDIA_ERR[this.audioElement.error.code as keyof typeof MEDIA_ERR]
      : ''

  _handleAudioElementError = (): number => this.errorMessages.push(this._getAudioElementError())

  _ensureAudioElement = (): null | HTMLAudioElement => {
    if (!isBrowser) {
      throw new Error('No browser context')
    }

    if (!this.audioElement) {
      let audioEl = document.getElementById('audio-player-element-1') as HTMLAudioElement
      if (!audioEl) {
        audioEl = document.createElement('audio') as HTMLAudioElement
        if (!audioEl) {
          this.errorMessages.push('Could not create AudioElement')
          return null
        }
        audioEl.id = 'audio-player-element-1'
        audioEl.controls = false
        audioEl.crossOrigin = 'anonymous'
        audioEl.preload = 'auto'
        audioEl.autoplay = true
        audioEl.onerror = this._handleAudioElementError
        this.audioElement = audioEl
        document.querySelector('body')?.appendChild(this.audioElement)
      }
    }
    return this.audioElement
  }

  _destroyAudioElement = () =>
    document.querySelector && document.querySelector('body')?.removeChild(document.getElementById('audio-player-element-1') as Node)

  _stopAllActions = (): boolean => {
    this.stopLoop()
    this.resetState()
    return true
  }

  _loadTrack(trackUrl: string): Promise<void> {
    this._stopAllActions()
    const el = this._ensureAudioElement()
    if (!el || this.errorMessages.length) {
      return Promise.reject()
    }
    this.soundBuffer = null
    this.trackUrl = trackUrl
    el.src = trackUrl
    el.volume = this.volume
    return el.play()
  }

  _loadFileToBuffer = (): Promise<void> => {
    return new Promise((accept, reject) => {
      this.req = new XMLHttpRequest()
      this.req.open('get', this.trackUrl, true)
      this.req.responseType = 'arraybuffer'
      this.req.onload = () => {
        const data = this.req?.response
        if (data) {
          this.context
            ?.decodeAudioData(data)
            .then(buffer => {
              this.soundBuffer = buffer
              accept()
            })
            .catch(reject)
        }
      }
      this.req.send()
    })
  }

  play = (trackUrl: string, position = -1): Promise<void> => {
    const changedTrack = trackUrl && trackUrl !== this.trackUrl

    if (position > -1) {
      if (!this.seekTo(position)) {
        return Promise.reject()
      }
    }
    if (changedTrack) {
      return this._loadTrack(trackUrl)
    }
    this.setPaused(false)
    return Promise.resolve()
  }

  seekTo = (position: number | string): boolean => {
    const { audioElement, errorMessages } = this
    if (audioElement) {
      if (audioElement.currentTime !== position) {
        const newTime = parseFloat(String(position))
        audioElement.currentTime = isNaN(newTime) ? 0 : newTime
      }
      return true
    }
    errorMessages.push('No AudioElement to seek in')
    return false
  }

  setBeatPositions(beatPositions: Array<number>): void {
    this.state.beatPositions = beatPositions
  }

  setupFilters = () => {
    // Create audio filters
    /*
    // NOTE(Kitti); Temp disabled as of tone14
    if (!this.highpassFilter) {
      this.source = this.context.createMediaElementSource(document.getElementsByTagName('audio')[0])
      this.highpassFilter = this.context.createBiquadFilter()
      this.highpassFilter.frequency.value = 0
      this.highpassFilter.type = 'highpass'
      this.lowpassFilter = this.context.createBiquadFilter()
      this.lowpassFilter.frequency.value = 20000
      this.lowpassFilter.type = 'lowpass'
      this.source.connect(this.highpassFilter)
      this.highpassFilter.connect(this.lowpassFilter)
      this.lowpassFilter.connect(this.context.destination)
    }
     */
  }

  /* eslint-disable */
  updateFilter = (values: [number, number]) => {
    /* eslint-enable */
    /*
      updateFilter = (values: [number, number]) => {
    // NOTE(Kitti); Temp disabled as of tone14
    const [lowFreq, highFreq] = values
    if (!isNaN(lowFreq)) {
      this.highpassFilter.frequency.setValueAtTime(lowFreq, this.context.currentTime)
    }
    if (!isNaN(highFreq)) {
      this.lowpassFilter.frequency.setValueAtTime(highFreq, this.context.currentTime)
    }
    */
  }

  updateVolume = (volume: number) => {
    if (isNaN(volume)) {
      return null
    }
    this.volume = volume
    return this.setVolume(volume)
  }

  playLoop = (loop: Loop) => {
    const { beatPositions } = this.state
    if (!beatPositions) {
      return null
    }
    const startTime = beatPositions[loop.beatStart]
    const endTime = beatPositions[loop.beatStart + loop.beatCount]

    this.stopLoop()

    this.player = new this.tone.Player(this.soundBuffer)
    this.setVolume(this.volume)
    this.loopStartedAtContextTime = this.player.context.currentTime
    this.player.start(this.tone.now(), startTime)
    this.player.loop = true
    this.player.loopStart = startTime
    this.player.loopEnd = endTime

    /*
    // NOTE(Kitti); Temp disabled as of tone14
    if (this.highpassFilter) {
      this.highpassFilter.disconnect()
      this.lowpassFilter.disconnect()
      this.player.chain(this.highpassFilter, this.lowpassFilter)
    }
    */
    this.player.toDestination()

    this.state.paused = false
    this.state.looping = true
    this.state.playing = true

    return this.player
  }

  pauseLoop = (): void => {
    this.state.playing = false
    if (this.player) {
      this.player.stop()
    }
  }

  stopLoop = (): void => {
    if (this.state.looping) {
      this.state.looping = false
      if (this.player) {
        this.player.dispose()
      }
    }
  }

  updateLoop = (beatStart: number | null | undefined, beatCount?: number): void => {
    const loop = new Loop()
    loop.trackId = this.currentLoop?.trackId || 0
    loop.beatStart = beatStart || beatStart === 0 ? beatStart : this.currentLoop?.beatStart || 0
    loop.beatCount = beatCount || beatCount === 0 ? beatCount : this.currentLoop?.beatCount || 0
    this.beatCount = loop.beatCount
    this.currentLoop = loop
    this.playLoop(loop)
  }

  loadTone = async () => {
    // NOTE(Kitti): Only load tone and context if not on old safari version
    this.tone = await toneImport.load()
    const { AudioContext } = (await standardizedAudioContextImport.load()) as unknown as { AudioContext: TAudioContextConstructor }
    // NOTE(Kitti): Need check of unit tests will fail
    if (window.AudioContext || window.webkitAudioContext) {
      this.context = new AudioContext()
      this.tone.setContext(this.context)
    }
    return Promise.resolve()
  }

  createLoop = (trackId: number, beatStart: number, beatCount: number): Promise<void> => {
    if (beatCount) {
      this.beatCount = beatCount
    }
    this.audioElement?.pause()
    const loop = new Loop()
    loop.beatStart = beatStart
    loop.trackId = trackId
    loop.beatCount = this.beatCount
    this.currentLoop = loop

    if (!this.soundBuffer) {
      return this._loadFileToBuffer()
        .then(() => {
          this.audioElement?.pause()
          this.playLoop(loop)
        })
        .catch(e => {
          console.log(e) // eslint-disable-line
        })
    } else {
      this.playLoop(loop)
      return Promise.resolve()
    }
  }
}

export const audioBackend = isBrowser ? new AudioBackend() : null

if (isBrowser) {
  window.audioBackend = audioBackend
}
